import { useRef, useState } from 'react';
import { Avatar, Icon, ProgressRing, type IconName } from '../../components/ui';

/**
 * The landing page's product preview: the real interface components, filled with a fixed sample of
 * a day at TechCore Solutions. Every panel says it is a sample, none of it is a real person's work,
 * and nothing here talks to the API.
 */

interface PreviewTab {
  id: string;
  label: string;
  icon: IconName;
  caption: string;
}

const TABS: PreviewTab[] = [
  { id: 'ticket', label: 'Ticket', icon: 'ticket', caption: 'A ticket arrives with the same detail a real queue gives you.' },
  { id: 'mentor', label: 'Mentor', icon: 'sparkles', caption: 'Ask why, not just what. The mentor answers in the moment.' },
  { id: 'progress', label: 'Progress', icon: 'target', caption: 'Skills build up from work you actually completed.' },
  { id: 'assignment', label: 'Assignment', icon: 'clipboard', caption: 'Your manager hands you the next piece of work.' },
  { id: 'dashboard', label: 'Workplace', icon: 'chart', caption: 'The service desk numbers your team watches all day.' },
];

export function ProductPreview() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (i: number) => {
    const next = (i + TABS.length) % TABS.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); select(active + 1); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); select(active - 1); }
    if (e.key === 'Home') { e.preventDefault(); select(0); }
    if (e.key === 'End') { e.preventDefault(); select(TABS.length - 1); }
  };

  const tab = TABS[active] as PreviewTab;
  return (
    <div
      className={`preview${paused ? ' paused' : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="frame">
        <div className="frame-bar">
          <span className="dots" aria-hidden="true"><i /><i /><i /></span>
          <span className="path">techcore.worksim/workplace</span>
          <span style={{ marginLeft: 'auto' }}><span className="badge cyan">Sample, not live data</span></span>
        </div>
        <div className="tabs" role="tablist" aria-label="Product preview" onKeyDown={onKeyDown}>
          {TABS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`preview-tab-${t.id}`}
              ref={(el) => { tabRefs.current[i] = el; }}
              aria-selected={i === active}
              aria-controls={`preview-panel-${t.id}`}
              tabIndex={i === active ? 0 : -1}
              onClick={() => setActive(i)}
            >
              <Icon name={t.icon} size={16} /> {t.label}
            </button>
          ))}
        </div>
        <div className="panel" role="tabpanel" id={`preview-panel-${tab.id}`} aria-labelledby={`preview-tab-${tab.id}`} tabIndex={0} key={tab.id}>
          {tab.id === 'ticket' && <TicketPanel />}
          {tab.id === 'mentor' && <MentorPanel />}
          {tab.id === 'progress' && <ProgressPanel />}
          {tab.id === 'assignment' && <AssignmentPanel />}
          {tab.id === 'dashboard' && <DashboardPanel />}
        </div>
      </div>
      <p className="small muted" style={{ marginTop: 'var(--sp-3)' }} aria-live="polite">{tab.caption}</p>
    </div>
  );
}

function TicketPanel() {
  return (
    <>
      <div className="ticket-head">
        <div>
          <span className="ticket-id">TCK-2001</span>
          <h3 style={{ margin: '0.1rem 0 0' }}>Locked out of account, month-end close today</h3>
        </div>
        <div className="row" style={{ gap: '0.3rem' }}>
          <span className="badge bad">High priority</span>
          <span className="badge accent">In progress</span>
        </div>
      </div>
      <dl className="kv">
        <dt>Raised by</dt>
        <dd>Priya Raman, Finance</dd>
        <dt>Assigned to</dt>
        <dd>You, IT Support</dd>
        <dt>Waiting</dt>
        <dd>18 minutes</dd>
      </dl>
      <p className="small" style={{ margin: 0 }}>
        &ldquo;I have tried my password three times and now nothing works. I need the ledger before the close meeting at 14:00.&rdquo;
      </p>
      <ul className="checklist">
        <li><span className="mark"><Icon name="check" size={16} /></span> Verified identity against the directory</li>
        <li><span className="mark"><Icon name="check" size={16} /></span> Checked the lockout policy in the knowledge base</li>
        <li><span className="mark todo"><Icon name="clock" size={16} /></span> Decide: reset, unlock, or escalate to Identity</li>
      </ul>
    </>
  );
}

function MentorPanel() {
  return (
    <>
      <p className="small muted sim-label" style={{ margin: 0 }}>
        <Icon name="info" size={14} /> Simulated mentor exchange. Sample wording, written for this preview.
      </p>
      <div className="bubble you">
        <span className="who">You</span>
        Her account is locked, not disabled. Do I reset the password or just unlock it?
      </div>
      <div className="bubble mentor">
        <span className="who"><Avatar name="AI Mentor" size="sm" /> AI mentor</span>
        Check what caused the lockout first. If it was repeated failed attempts from her own laptop, unlocking is enough and keeps her working. A reset is the right call when you cannot explain the failures, because then you cannot rule out someone else trying the account.
      </div>
      <p className="xs muted" style={{ margin: 0 }}>Guidance for practice. It is not IT advice for a real system.</p>
    </>
  );
}

function ProgressPanel() {
  return (
    <>
      <div className="ring-row">
        <ProgressRing value={0.68} label="Help Desk Analyst track" size={104} />
        <div>
          <h3 style={{ margin: 0 }}>Help Desk Analyst</h3>
          <p className="small muted" style={{ margin: '0.15rem 0 0' }}>Sample learner · 7 scenarios completed · 4 skills with evidence</p>
          <div className="row" style={{ gap: '0.3rem', marginTop: 'var(--sp-2)' }}>
            <span className="badge tier">foundational</span>
            <span className="badge">Practicing: incident triage</span>
          </div>
        </div>
      </div>
      <div>
        <div className="small muted">Ticket documentation</div>
        <div className="meter ok"><span style={{ width: '82%' }} /></div>
      </div>
      <div>
        <div className="small muted">Customer communication</div>
        <div className="meter"><span style={{ width: '64%' }} /></div>
      </div>
      <p className="xs muted" style={{ margin: 0 }}>Progress describes practice inside the simulator. It is not a qualification.</p>
    </>
  );
}

function AssignmentPanel() {
  return (
    <>
      <div className="row" style={{ gap: 'var(--sp-3)' }}>
        <Avatar name="Dana Whitfield" size="lg" />
        <div>
          <strong>Dana Whitfield</strong>
          <div className="small muted">Service Desk Manager · IT Support</div>
        </div>
        <span className="badge warn" style={{ marginLeft: 'auto' }}>Due today</span>
      </div>
      <div className="notice small" style={{ margin: 0 }}>
        &ldquo;Take TCK-2001 before the close meeting. Write the resolution note so the next person on shift can follow it without asking you.&rdquo;
      </div>
      <dl className="kv">
        <dt>Deliverable</dt>
        <dd>Ticket resolution note</dd>
        <dt>Scenario</dt>
        <dd>A locked-out user on month-end close</dd>
        <dt>Estimated</dt>
        <dd>About 25 minutes</dd>
      </dl>
      <div className="row"><span className="button small" aria-hidden="true">Open assignment</span><span className="small muted">Sample assignment card</span></div>
    </>
  );
}

function DashboardPanel() {
  const metrics = [
    { label: 'Open tickets', value: '14', delta: 'Queue steady' },
    { label: 'Breaching in 1h', value: '2', delta: 'Both with Identity' },
    { label: 'First contact fix', value: '71%', delta: 'Target 70%' },
  ];
  return (
    <>
      <div className="metric-row">
        {metrics.map((m) => (
          <div className="metric" key={m.label}>
            <div className="stat">
              <span className="value" style={{ fontSize: 'var(--fs-xl)' }}>{m.value}</span>
              <span className="label">{m.label}</span>
            </div>
            <span className="xs muted">{m.delta}</span>
          </div>
        ))}
      </div>
      <h4 style={{ margin: 0 }}>Today at the service desk</h4>
      <ul className="checklist">
        <li><span className="mark"><Icon name="mail" size={16} /></span> Finance asks for an update on the ledger lockouts</li>
        <li><span className="mark"><Icon name="users" size={16} /></span> Identity team picks up two escalations</li>
        <li><span className="mark todo"><Icon name="flag" size={16} /></span> Change freeze starts at 16:00</li>
      </ul>
      <p className="xs muted" style={{ margin: 0 }}>Sample dashboard for the virtual company.</p>
    </>
  );
}
